/**
 * OAuthApiClient -- la autorizacion del MCP de Nexo que Josse decide (MCP-NEXO-OAUTH-T1, D5 de Ares #23523).
 *
 * Cuando ChatGPT pide entrar al MCP, la API crea una SOLICITUD y manda a Josse al panel
 * (#/oauth/autorizar?solicitud=<uuid>). Aqui se lee la solicitud y se decide. Contrato leido del codigo de nexo-api
 * PR 448 (bf757e2, Controllers/OAuthController.cs):
 *
 *   GET  /oauth/solicitudes/{id}      [RequireAdmin]
 *        -> { solicitudId, cliente, scopesPedidos[], resource, venceAt, decidida, agentePropuesto, scopesQueRecibiria[],
 *             credencialPropuestaId, credencialPropuestaPrefijo }  (la llave por defecto; NO trae el sujeto, medido en main 4ef75c2)
 *           (+ clienteId, clienteRegistradoAt, creadaAt cuando entre el PR b, #23550)
 *        404 not_found
 *   GET  /oauth/solicitudes/{id}?credencial=<id>   (#42497 de A): la MISMA vista previa recalculada con la llave elegida
 *        (agentePropuesto, credencialPropuestaId y scopesQueRecibiria de esa llave, con la regla de DecidirAsync)
 *        400 credencial_de_otro_sujeto. Una API sin el parametro lo ignora y responde con la propuesta: la vista lo
 *        distingue porque credencialPropuestaId no es la elegida.
 *   POST /oauth/authorize/decision    [RequireAdmin]  { solicitudId, aprobar, credentialId? }
 *        -> { redirect }   el panel navega ahi (con el codigo, o con error=access_denied si se nego)
 *        404 not_found | 409 already_decided | expired | 400 no_credential | no_scopes
 *
 * Es de la SESION de admin: una llave nunca decide. Nunca lanza.
 */
import { ApiBase } from './ApiBase.js';

const texto = (v) => (v === null || v === undefined ? '' : String(v));
const lista = (v) => (Array.isArray(v) ? v.map(texto) : []);
const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};

/**
 * D1 de la API (OAuthReglas.RedirectPermitido): solo se devuelve un codigo a https, host EXACTO chatgpt.com o
 * chat.openai.com, sin fragmento y sin usuario. El panel aplica LA MISMA regla antes de navegar: si un dia la API
 * devolviera otra cosa, la pantalla la muestra y no la sigue. Defensa en profundidad, no desconfianza.
 */
export const HOSTS_DE_REDIRECT = ['chatgpt.com', 'chat.openai.com'];
export function redirectSeguro(url) {
  let u;
  try { u = new URL(texto(url)); } catch { return false; }
  if (u.protocol !== 'https:' || u.hash || u.username || u.password) return false;
  return HOSTS_DE_REDIRECT.includes(u.hostname.toLowerCase());
}

/**
 * Si la solicitud ya se decidio. La API del 448 manda un BOOLEANO (s.Decidida != null): una pendiente llega como
 * `false`, no como null. Lo cazo Q (#23557): yo comparaba con null y una pendiente salia "ya decidida", sin botones.
 * Se tolera tambien una fecha (si un dia manda decidida_at), pero false y null son pendiente.
 */
export function decididaDe(v) {
  return v === true || (typeof v === 'string' && v.trim() !== '');
}

/** El id de la solicitud desde el hash (#/oauth/autorizar?solicitud=<uuid>). '' si no viene o no es un uuid. */
export function solicitudDelHash(hash) {
  const q = texto(hash).split('?')[1] || '';
  const id = new URLSearchParams(q).get('solicitud') || '';
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ? id : '';
}

function motivo(res) {
  const cuerpo = res?.details ?? {};
  const codigo = texto(cuerpo.message ?? '') + ' ' + texto(res?.error);
  const n = Number(res?.code ?? res?.status);
  if (n === 401) return { motivo: 'SIN_SESION', detalle: 'la sesion vencio: vuelve a entrar al panel y abre otra vez el enlace' };
  if (n === 403) return { motivo: 'PROHIBIDO', detalle: 'esta autorizacion es de la sesion de admin de Josse' };
  if (/already_decided/.test(codigo)) return { motivo: 'YA_DECIDIDA', detalle: 'esta solicitud ya se decidio' };
  if (/expired/.test(codigo)) return { motivo: 'VENCIDA', detalle: 'la solicitud vencio: ChatGPT tiene que volver a pedirla' };
  if (/no_credential/.test(codigo)) return { motivo: 'SIN_LLAVE', detalle: 'no hay una llave viva para este asistente: emitela en Accesos de asistentes (#/contexto)' };
  if (/credencial_de_otro_sujeto/.test(codigo)) return { motivo: 'LLAVE_AJENA', detalle: 'esa llave es de otra persona: no puede entrar por esta solicitud' };
  if (/no_scopes/.test(codigo)) return { motivo: 'SIN_PERMISOS', detalle: 'con esa llave y tu consentimiento de hoy el acceso no tendria ningun permiso: amplialo en #/contexto' };
  if (/not_found/.test(codigo)) return { motivo: 'NO_EXISTE', detalle: 'esa solicitud no existe' };
  if (n === 404) return { motivo: 'NO_PUBLICADO', detalle: 'la API servida todavia no tiene la autorizacion del MCP (nexo-api PR 448)' };
  return { motivo: 'ERROR_API', detalle: texto(cuerpo.detail) || texto(res?.error) || 'no pudimos hablar con la API ahora' };
}

export const OAuthApiClient = {
  /** La solicitud; con `credencial`, la vista previa recalculada con esa llave (#42497). */
  async leerSolicitud(id, credencial = '') {
    if (!texto(id)) return { ok: false, motivo: 'SIN_SOLICITUD', detalle: 'el enlace no trae una solicitud valida' };
    const conLlave = texto(credencial) ? `?credencial=${encodeURIComponent(texto(credencial))}` : '';
    let res;
    try { res = await ApiBase.get(`/oauth/solicitudes/${encodeURIComponent(id)}${conLlave}`); }
    catch (err) { return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' }; }
    if (!res.ok) return { ok: false, ...motivo(res) };
    const d = res.data?.data ?? res.data ?? {};
    return {
      ok: true,
      id: texto(pick(d, 'solicitudId', 'SolicitudId')) || id,
      cliente: texto(pick(d, 'cliente', 'Cliente')),
      clienteId: texto(pick(d, 'clienteId', 'ClienteId')),
      clienteRegistrado: pick(d, 'clienteRegistradoAt', 'ClienteRegistradoAt'),
      creada: pick(d, 'creadaAt', 'CreadaAt'),
      vence: pick(d, 'venceAt', 'VenceAt'),
      decidida: decididaDe(pick(d, 'decidida', 'Decidida')),
      agente: texto(pick(d, 'agentePropuesto', 'AgentePropuesto')),
      // OAUTH-ELEGIR-LLAVE-T1: con que llave se ataria por defecto (COORD de Ares #23756; la API ya lo manda).
      credencialPropuestaId: texto(pick(d, 'credencialPropuestaId', 'CredencialPropuestaId')),
      credencialPropuestaPrefijo: texto(pick(d, 'credencialPropuestaPrefijo', 'CredencialPropuestaPrefijo')),
      pedidos: lista(pick(d, 'scopesPedidos', 'ScopesPedidos')),
      recibiria: lista(pick(d, 'scopesQueRecibiria', 'ScopesQueRecibiria')),
      recurso: texto(pick(d, 'resource', 'Resource'))
    };
  },

  /** Aprueba o niega. Devuelve { ok, redirect, seguro }: el panel solo navega si `seguro`. */
  async decidir(id, aprobar, credentialId = null) {
    if (!texto(id)) return { ok: false, motivo: 'SIN_SOLICITUD', detalle: 'no hay solicitud que decidir' };
    const cuerpo = { solicitudId: texto(id), aprobar: aprobar === true };
    if (texto(credentialId)) cuerpo.credentialId = texto(credentialId);
    let res;
    try { res = await ApiBase.post('/oauth/authorize/decision', cuerpo); }
    catch (err) { return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' }; }
    if (!res.ok) return { ok: false, ...motivo(res) };
    const d = res.data?.data ?? res.data ?? {};
    const redirect = texto(pick(d, 'redirect', 'Redirect'));
    return { ok: true, redirect, seguro: redirectSeguro(redirect) };
  }
};
